import { messageService } from '@classes/messageService';

import { ErrorCodes } from '../enums/errorCodes';
import { RUStrings } from '../localization/ruRU';

export interface ApiError {
    statusCode: number;
    errorCode?: string;
    errorMessages?: string[];
    message?: string;
}

export class ErrorHandlingManager {
    /**
     * Парсинг ответа API об ошибке в стандартизованный формат
     */
    static parseApiError(statusCode: number, data: any): ApiError {
        if (statusCode >= 502 && statusCode <= 504) {
            return {
                statusCode,
                errorMessages: ['Сервер временно недоступен. Попробуйте позже.'],
            };
        }

        if (data == null || typeof data !== 'object' || Array.isArray(data)) {
            const generic =
                statusCode >= 500
                    ? 'Сервер временно недоступен. Попробуйте позже.'
                    : 'Не удалось обработать ответ сервера.';
            return {
                statusCode,
                errorMessages: [generic],
            };
        }

        // Формат исключения сервера
        if (Object.keys(data).includes('exception')) {
            return {
                statusCode,
                errorCode: data?.errorCode,
                errorMessages: ['Ошибка сервера'],
            };
        }
        // Формат ошибок валидации
        else if (Object.keys(data).includes('errors')) {
            return {
                statusCode,
                errorCode: data?.errorCode,
                errorMessages: Object.values(data?.errors),
            };
        }
        // Стандартный формат ошибки
        else {
            const code = data?.errorCode;
            const fromCode =
                code != null && Object.keys(ErrorCodes).includes(code)
                    ? RUStrings[code as keyof typeof RUStrings]
                    : null;
            const raw = fromCode ?? (code != null ? String(code) : null);
            const messageText = data?.message != null ? String(data.message) : null;
            const single =
                messageText ||
                raw ||
                (statusCode >= 500
                    ? 'Сервер временно недоступен. Попробуйте позже.'
                    : 'Ошибка запроса');
            return {
                statusCode,
                errorCode: code,
                errorMessages: [single],
                message: messageText ?? undefined,
            };
        }
    }

    /**
     * Создает стандартизированную ошибку API и выбрасывает ее
     * Не отображает пользователю - только создает объект ошибки для перехвата выше
     */
    static createAndThrow(error: ApiError): never {
        throw new Error(ErrorHandlingManager.formatApiErrorMessage(error));
    }

    /**
     * Обработка ошибок API и отображение соответствующих сообщений пользователю
     * Должен вызываться только в одном месте - на уровне обработчика запросов
     */
    static handleApiError(error: ApiError | Error): void {
        if (error instanceof Error) {
            console.error('API Error:', error.message);
            messageService.sendError(error.message);
            return;
        }

        const errorMessages = error.errorMessages || [
            error.message ? error.message : 'Неопределенная ошибка',
        ];
        console.error(`API Error (${error.statusCode}):`, errorMessages.join(', '));
        messageService.sendErrorList([errorMessages]);
    }

    /**
     * Создание сообщения об ошибке из объекта ApiError
     */
    static formatApiErrorMessage(error: ApiError): string {
        const messages =
            error.errorMessages?.join(', ') ||
            (error?.message ? error.message : 'Неопределенная ошибка');
        return `${messages} (${error.statusCode})`;
    }
}
